import t from 'react-translate';
import moment from 'moment';
import { css } from '@emotion/react';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { gray6, hexToRgbaString, teal } from 'styles/global_defaults/colors';
import { dunes, oasis, sandstone } from 'athena/styles/colors';
import {
  halfSpacing,
  quarterSpacing,
} from 'styles/global_defaults/scaffolding';
import { MentoringProgramActionItem } from 'redux/schemas/models/mentoring-program-action-items';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';
import { config } from '@config/pendo.config.json';
import SwitchAssigneeOption from 'athena/components/switch-assignee-option';
import NvTooltip from 'shared/components/nv-tooltip';
import SessionMenuOption from './session-menu-option';
import { optionStyles } from './styles';
import DeleteActionOption from './delete-action-option';
import { DELETE_BUTTON_SPACER_WIDTH } from '../constants';

type ActionMenuProps = {
  item: MentoringProgramActionItem;
  disabled: boolean;
  isCompleted: boolean;
  isEditing: boolean;
  isReadOnly?: boolean;
  showPreAssignedBadge?: boolean;
  assignedTo: number;
  sessionId: number;
  enrollments?: { [id: number]: MentoringProgramEnrollment };
  onChange: () => void;
  onDelete: () => void;
  showPlanSessionFlyout?: (session: MentorshipProgramSession) => void;
};

const editStyles = css`
  ${optionStyles}
  margin: 1px;
  padding: ${quarterSpacing}px;

  &.disabled {
    pointer-events: none;
    background: ${gray6} !important;
  }
`;

const baseBadgeStyles = css`
  border-radius: 6px;
  padding: 4px 12px;
`;

const requiredBadgeStyles = css`
  ${baseBadgeStyles}
  background: ${hexToRgbaString(dunes, 0.3)};
  color: ${sandstone};
`;

const suggestedBadgeStyles = css`
  ${baseBadgeStyles}
  background: ${hexToRgbaString(teal, 0.3)};
  color: ${oasis};
`;

const menuStyles = css`
  gap: ${halfSpacing}px;
`;

// Reserves the same trailing space DeleteActionOption takes up in editable rows, so
// read-only rows (which never render a delete button) stay aligned with owned rows
// in the same list. Only rendered when showPreAssignedBadge is honored (the standard
// Action Items list) — summary views that opt out of the badge don't mix with owned
// rows, so there's nothing to align with there.
const deleteButtonSpacerStyles = css`
  width: ${DELETE_BUTTON_SPACER_WIDTH}px;
  flex-shrink: 0;
`;

const ActionMenu = ({
  item,
  disabled,
  isCompleted,
  isEditing,
  isReadOnly = false,
  showPreAssignedBadge = true,
  assignedTo,
  sessionId,
  enrollments,
  onChange,
  onDelete,
  showPlanSessionFlyout,
}: ActionMenuProps) => {
  const { id: itemId, completedAt, completedBy } = item;
  const dataQATagBase = config.pendo.athena.mentorshipProgram.participantHome.actionItems.item;
  const dataQATag = itemId ? dataQATagBase.updateActionItemCheckButton : dataQATagBase.createNewActionItemAddButton;
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS.ARIA_LABEL;
  const ariaLabel = itemId ? translationBase.UPDATE_ACTION_ITEM() : translationBase.ADD_ACTION_ITEM();
  const actionItemTranslations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS.ITEM.TOOLTIP;
  const completedTooltip = isCompleted && completedBy && completedAt
    ? actionItemTranslations.COMPLETED_BY(moment(completedAt).format(actionItemTranslations.DATE_FORMAT()), completedBy.user.fullName)
    : undefined;
  const switchAssigneeDataQATag = dataQATagBase.editActionItemAssignedTo;

  if (isReadOnly) {
    if (!item.assignedTo) return null;

    const preAssignedBadge = item.isPreAssigned && showPreAssignedBadge ? (
      <NvTooltip
        text={item.required
          ? actionItemTranslations.REQUIRED_BADGE()
          : actionItemTranslations.SUGGESTED_BADGE()}
      >
        <div
          css={item.required ? requiredBadgeStyles : suggestedBadgeStyles}
          className='d-flex align-items-center justify-content-center text-small font-weight-bold text-nowrap'
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- makes the badge focusable so its tooltip is reachable via keyboard/touch
          tabIndex={0}
        >
          {item.required
            ? t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS.ITEM.REQUIRED_BADGE()
            : t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS.ITEM.SUGGESTED_BADGE()}
        </div>
      </NvTooltip>
    ) : null;

    return (
      <div css={menuStyles} className='d-flex align-items-center'>
        {preAssignedBadge}
        <SwitchAssigneeOption
          itemId={itemId}
          assignedTo={item.assignedTo.id}
          enrollments={{ [item.assignedTo.id]: item.assignedTo as MentoringProgramEnrollment }}
          dataQATag={switchAssigneeDataQATag}
          completedTooltip={completedTooltip}
          isReadOnly
        />
        {showPreAssignedBadge && <div aria-hidden='true' css={deleteButtonSpacerStyles} />}
      </div>
    );
  }

  if (isEditing) {
    return (
      <ClickableContainer
        disabled={disabled}
        css={editStyles}
        className={disabled ? 'disabled' : ''}
        onClick={onChange}
        data-qa={dataQATag}
        data-qa-id={`${dataQATag}-${itemId}`}
        aria-label={ariaLabel}
      >
        <NvIcon icon={itemId ? 'check' : 'create-new-post'} size='smallest' />
      </ClickableContainer>
    );
  }

  if (itemId) {
    return (
      <div css={menuStyles} className='d-flex align-items-center'>
        {sessionId && (
          <SessionMenuOption
            actionItemId={itemId}
            sessionId={sessionId}
            showPlanSessionFlyout={showPlanSessionFlyout}
          />
        )}
        <SwitchAssigneeOption
          itemId={itemId}
          assignedTo={assignedTo}
          enrollments={enrollments}
          dataQATag={switchAssigneeDataQATag}
          completedTooltip={completedTooltip}
        />
        <DeleteActionOption actionItemId={itemId} onDelete={onDelete} />
      </div>
    );
  }
  return null;
};

export default ActionMenu;
